<script setup lang="ts">
  import { sanitizeUrl } from '@braintree/sanitize-url';
  import { LinkVariant } from '@tundr/theme';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed } from 'vue';
  import { FontIcon } from '../FontIcon';
  import {
    linkArrowClass,
    linkContainerClass,
    linkSizeVariants,
    linkTextVariants,
    linkVariants,
  } from './Link.css';
  import { LinkSize, LinkTarget } from './Link.types';

  defineEmits<{
    click: [];
  }>();

  const isValidUrl = (string: string): boolean => {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  export type LinkProps = {
    id?: string;
    testId?: string;
    title?: string;
    target?: LinkTarget;
    variant?: LinkVariant;
    href?: string;
    showArrow?: boolean;
    size?: LinkSize;
  };

  const props = withDefaults(defineProps<LinkProps>(), {
    target: LinkTarget.BLANK,
    variant: LinkVariant.primary,
    showArrow: false,
    size: LinkSize.default,
  });

  const sanitizedUrl = computed(() => {
    if (!props.href || !isValidUrl(props.href)) return undefined;

    return sanitizeUrl(props.href);
  });

  const linkClasses = computed(() => [
    linkVariants[props.variant],
    linkSizeVariants[props.size],
  ]);
</script>

<template>
  <div :class="linkContainerClass">
    <a
      :id="id"
      :data-test-id="testId"
      :title="title"
      :class="linkClasses"
      :href="sanitizedUrl"
      :target="target"
      @click="$emit('click')"
    >
      <span :class="linkTextVariants[variant]"><slot /></span>
      <FontIcon
        v-if="showArrow"
        :icon-name="TUNDR_ICONS.CHEVRON_RIGHT"
        :class="linkArrowClass"
      />
    </a>
  </div>
</template>
